import React, { FC, useState, useRef } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import Skeleton from '@mui/material/Skeleton';
import { Grid, Typography } from '@mui/material';
import { AddressElement, Elements } from '@stripe/react-stripe-js';
import type { Stripe, StripeElements, StripePaymentElementChangeEvent } from '@stripe/stripe-js';
import { StripePaymentElement } from 'views/rebranded-module/common/components/Logical/StripePaymentElement';

interface StripePaymentSectionProps {
  stripePromise: Promise<Stripe | null> | null;
  clientSecret: string | null;
  customerSessionClientSecret?: string | null;
  currency?: string | null;
  setStripe: Dispatch<SetStateAction<Stripe | null>>;
  setElements: Dispatch<SetStateAction<StripeElements | null>>;
  onPaymentMethodChange?: (event: StripePaymentElementChangeEvent) => void;
  onAddressChange?: (event: any) => void;
  paymentElementOptions?: Record<string, unknown>;
  allowedCountries?: string[];
  defaultAddressValues?: Record<string, unknown>;
  billingAddressLabel?: string;
  paymentDetailsLabel?: string;
  showAddressElement?: boolean;
}

const StripePaymentSection: FC<StripePaymentSectionProps> = ({
  stripePromise,
  clientSecret,
  customerSessionClientSecret,
  currency,
  setStripe,
  setElements,
  onPaymentMethodChange,
  onAddressChange,
  paymentElementOptions,
  allowedCountries,
  defaultAddressValues,
  billingAddressLabel = 'Billing Address',
  paymentDetailsLabel = 'Payment Details',
  showAddressElement = false,
}) => {
  const [showAddress, setShowAddress] = useState(false);
  const initialDefaultAddressValues = useRef(defaultAddressValues);

  const handlePaymentMethodChange = (event: StripePaymentElementChangeEvent) => {
    // value.payment_method is present (runtime) when a saved PM is selected; absent for new card entry.
    // Not typed in stripe-js 1.42.0 — cast required.
    const isSavedCard = !!(event.value as any).payment_method;
    const isCard = event.value.type === 'card';
    setShowAddress(isCard && !isSavedCard);
    onPaymentMethodChange?.(event);
  };

  if (!clientSecret || !stripePromise) {
    return (
      <>
        <Grid container spacing={2} data-testid="checkout-stripe-payment-loading">
          <Grid item xs={12}>
            <Skeleton variant="rectangular" width="100%" height={250} />
          </Grid>
        </Grid>
      </>
    );
  }

  return (
    <Elements
      key={clientSecret}
      stripe={stripePromise}
      options={{
        clientSecret,
        ...(customerSessionClientSecret ? { customerSessionClientSecret } : {}),
        ...(currency ? { currency: currency.toLowerCase() } : {}),
        appearance: {
          variables: {
            colorPrimary: 'black',
            colorText: 'black',
            colorTextSecondary: 'black',
          },
          rules: {
            '.AccordionItem': {
              fontWeight: '400',
            },
            '.AccordionItem:hover': {
              fontWeight: '500',
            },
            '.AccordionItem--selected': {
              fontWeight: '700',
            },
          },
        },
      }}
    >
      {showAddress && showAddressElement && (
        <div data-testid="checkout-stripe-address-element">
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            {billingAddressLabel}
          </Typography>
          <AddressElement
            options={{
              mode: 'billing',
              fields: { phone: 'always' },
              validation: { phone: { required: 'always' } },
              ...(allowedCountries?.length ? { allowedCountries } : {}),
              ...(initialDefaultAddressValues.current ? { defaultValues: initialDefaultAddressValues.current } : {}),
            }}
            onChange={onAddressChange}
          />
          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
            {paymentDetailsLabel}
          </Typography>
        </div>
      )}
      <div data-testid="checkout-stripe-payment-element">
        <StripePaymentElement
          setStripe={setStripe}
          setElements={setElements}
          options={{
            layout: {
              type: 'accordion',
              defaultCollapsed: false,
              radios: true,
              spacedAccordionItems: false,
            },
            ...paymentElementOptions,
          }}
          onChange={showAddressElement ? handlePaymentMethodChange : onPaymentMethodChange}
        />
      </div>
    </Elements>
  );
};

export { StripePaymentSection };